import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { navigate } from 'vike/client/router';

import FormAlert from '@/components/Forms/FormAlert';
import type { WithFormErrors } from '@/components/Forms/FormControl';
import FormControl from '@/components/Forms/FormControl';
import FormErrors from '@/components/Forms/FormErrors';
import FormSubmitButton from '@/components/Forms/FormSubmitButton';
import useFormSubmit from '@/components/Forms/useFormSubmit';
import { Button } from '@/components/react-aria/button';
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from '@/components/react-aria/dialog';
import { Modal } from '@/components/react-aria/modal';
import { Strong, Text } from '@/components/react-aria/text';
import { useVisualViewportHeight } from '@/renderer/utils/hooks/useVisualViewportHeight';
import { beginSignOutTransition, endSignOutTransition } from '@/renderer/utils/signOutTransition';
import useMe, { authToken } from '@/renderer/utils/useMe';
import { useRequest } from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

// Body shape the server expects: an explicit `confirm: 'DELETE'` intent guard
// (the authenticated session is the real authorization).
type DeleteAccountBody = { data: { confirm: string; reason?: string } };

const deleteAccountRequest: RequestSchema<{ deleted?: boolean }, DeleteAccountBody> = {
  name: 'deleteAccountRequest',
  input: '/api/v1/user/delete/me',
  method: 'POST',
};

/**
 * Account deletion (App Store Guideline 5.1.1(v)). Rendered for ALL platforms —
 * it must be visible in the iOS reader app, so it is never gated behind
 * isIosNative(). A confirmation dialog (type DELETE) guards against an accidental
 * tap; on success the server has already anonymized the account, canceled any
 * subscription, and invalidated every session, so we clear local auth and send
 * the user to sign-in.
 */
export default function DeleteAccountForm() {
  const form = useForm<WithFormErrors<{ confirm: string }>>();
  const [, setUser] = useMe(true, false);

  // Keep --support-vh synced to the visible viewport so the confirmation sheet
  // rises above the on-screen keyboard promptly on iOS (the hook re-reads across
  // a rAF + 300ms tail, covering the keyboard-open animation). See the Modal's
  // keyboard-aware overlay height below (Bug 3 in the App Store deletion clip).
  useVisualViewportHeight();

  const request = useRequest(deleteAccountRequest, { manual: true, hideAlert: true });

  const [deleted, setDeleted] = useState(false);

  const confirmValue = form.watch('confirm');
  const canDelete = confirmValue?.trim().toUpperCase() === 'DELETE';

  const handleFormSubmit = useFormSubmit(
    {
      form,
      callback: async () => {
        await request.run({ data: { confirm: 'DELETE' } }, { throwOnRun: true });

        // Deletion succeeded. Enter the sign-out transition BEFORE clearing auth
        // so the teardown refetches that now 401/404 (the account is gone) are
        // swallowed instead of surfacing a scary "record does not exist" toast —
        // which, in the App Store deletion clip, reads as the deletion failing.
        // It also keeps the RAS loader from flashing on the way to /signin.
        beginSignOutTransition();

        // The server has already invalidated every session, so clear local auth.
        setDeleted(true);
        setUser?.(null);
        authToken(null);

        // Hold the calm success confirmation briefly, then land on sign-in.
        // Unwind the transition marker a beat after the nav so a stray late alert
        // can't reach the user, but it can never stay suppressed indefinitely.
        setTimeout(() => navigate('/signin'), 3500);
        setTimeout(endSignOutTransition, 4500);
      },
    },
    [setUser],
  );

  if (deleted) {
    return (
      <span className="text-emerald-600 text-sm dark:text-emerald-400">
        Your account has been deleted. Signing you out...
      </span>
    );
  }

  return (
    <DialogTrigger>
      <Button variant="unstyle">
        <span className="font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">
          Delete account
        </span>
      </Button>
      <Modal
        size="md"
        classNames={{
          // Keyboard-aware height: drive the overlay off --support-vh (kept in
          // sync by useVisualViewportHeight above) so the bottom-sheet confirm
          // dialog sits right above the iOS keyboard, and transition the height
          // change so it glides into place instead of jumping late. Falls back to
          // the full dynamic viewport before the hook's first sync.
          modalOverlay:
            'h-[var(--support-vh,100dvh)] transition-[height] duration-200 ease-out motion-reduce:transition-none',
        }}
      >
        <Dialog className="!focus-visible:outline-none">
          <DialogHeader>Delete your account</DialogHeader>
          <DialogCloseButton />
          <DialogBody>
            <form
              onSubmit={handleFormSubmit}
              method="POST"
              className="space-y-6"
              id="delete-account"
            >
              <Text>
                This <Strong>permanently deletes</Strong> your RAS account and cannot be undone.
                Your personal information is removed and you are signed out on every device. Any
                active subscription is canceled so you will not be billed again.
              </Text>
              <div className="mt-4">
                <FormControl
                  form={form}
                  name="confirm"
                  label="Type DELETE to confirm"
                  autoComplete="off"
                  aria-autocomplete="none"
                  autoFocus
                />
                <FormErrors
                  form={form}
                  request={request}
                  // The request body ({ data: { confirm } }) is a different shape
                  // from the form field (confirm), so opt out of the form<->request
                  // shape check; errors are still surfaced from request.error.
                  ignoreMismatch
                  rewrite={(err) => (
                    <FormAlert>
                      {err.toString() || 'Could not delete your account. Please try again.'}
                    </FormAlert>
                  )}
                />
              </div>
            </form>
          </DialogBody>
          <DialogFooter>
            <DialogCloseButton className="ring-1 ring-zinc-900/10 dark:ring-white/10">
              Cancel
            </DialogCloseButton>

            {/*
              Submit via an explicit onClick rather than the `form=` attribute.
              This button sits in the DialogFooter, outside the <form> in the
              DialogBody; associating it to the form by id submits fine on desktop
              but is a no-op on iOS Safari/WebKit (a long-standing bug), which
              left the button dead on iPhone. Calling the submit handler directly
              (type="button") works identically on every platform. The form's own
              onSubmit still handles the Return key.
            */}
            <FormSubmitButton
              className="group relative inline-flex items-center justify-center gap-x-2 whitespace-nowrap rounded-sm bg-red-600 px-3 py-[calc(--spacing(2.5)-var(--border-with,1px))] font-semibold text-base/6 text-white outline-hidden [--border-with:0px] hover:bg-red-500 disabled:opacity-50 data-pending:opacity-75 sm:py-[calc(--spacing(1.5)-var(--border-with,1px))] sm:text-sm/6"
              disabled={request.loading || !canDelete}
              loading={request.loading}
              type="button"
              onClick={handleFormSubmit}
            >
              Delete account
            </FormSubmitButton>
          </DialogFooter>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
}
